'use client';

import { useCallback, useId, useRef } from 'react';

import classnames from 'classnames';
import { IoRadioButtonOff, IoRadioButtonOn } from 'react-icons/io5';

import { IconButton } from '../../icon-button';
import type { RadioButtonProps } from './RadioButton.types';

import styles from './RadioButton.module.scss';

function RadioButton({
  style,
  className,
  checked = false,
  disabled = false,
  error = false,
  label,
  onChange,
  testId = 'ui-radio-button',
}: RadioButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  const dispatchChangeEvent = useCallback(() => {
    containerRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, []);

  const handleClick = useCallback(() => {
    if (onChange && !disabled) {
      onChange();
      dispatchChangeEvent();
    }
  }, [onChange, disabled, dispatchChangeEvent]);

  return (
    <div
      ref={containerRef}
      style={style}
      className={classnames(
        styles['radio-button'],
        error && styles['radio-button--error'],
        className,
      )}
      data-testid={testId}
    >
      <IconButton
        role="checkbox"
        aria-checked={checked}
        aria-labelledby={label ? labelId : undefined}
        icon={checked ? <IoRadioButtonOn /> : <IoRadioButtonOff />}
        size="sm"
        disabled={disabled}
        onClick={handleClick}
      />
      {label && (
        <div
          id={labelId}
          className={classnames(
            styles['label'],
            disabled && styles['label--disabled'],
            error && styles['label--error'],
            className,
          )}
        >
          {label}
        </div>
      )}
    </div>
  );
}

export { RadioButton };
